// RigSentinel · RC-1 — Floating Engineering Toolbar
//
// Single source of truth for the bottom-anchored action bar on every long
// engineering page. Renders up to ~6 context-sensitive icon-buttons plus a
// smooth-scroll Back-to-Top control that fades in after the user has
// scrolled past a threshold. Mobile-first, desktop-friendly.
//
// Usage:
//
//   <FloatingActionToolbar
//     actions={[
//       { id: "add",  icon: Plus,       label: "Add component", onClick: onAdd,  testId: "fab-add" },
//       { id: "save", icon: Save,       label: "Save",          onClick: onSave, testId: "fab-save",   primary: true },
//       { id: "sync", icon: RefreshCcw, label: "Synchronize",   onClick: onSync, testId: "fab-sync" },
//       { id: "print", href: "/print",  icon: Printer,          label: "Print",  testId: "fab-print" },
//     ]}
//   />
//
// Conventions:
//   * Items with a falsy `onClick` AND falsy `href` are HIDDEN (no clutter
//     for unavailable actions — matches the RC-1 spec).
//   * One `primary: true` item gets the amber filled style; the rest are
//     ghost-on-glass.
//   * Order in the array defines the visual (top-to-bottom) order. The
//     standard order is: ⬆ Top (auto-injected) · ➕ Add · 💾 Save · 🔄 Sync ·
//     🖨 Print · ✨ AI.
//
// Five-Defect Correction Pass, Defect 3 — this used to be a horizontal pill
// centered at the bottom of the viewport, which sat directly over whatever
// form row happened to be scrolled underneath its band. Repositioned to a
// vertical stack hugging the RIGHT edge (a standard mobile FAB-stack
// pattern) so it never spans across the readable content column.

import React, { useEffect, useState } from "react";
import { ArrowUp } from "lucide-react";
import { Link } from "react-router-dom";

const SCROLL_REVEAL_PX = 320;

export default function FloatingActionToolbar({ actions = [], testId = "floating-toolbar" }) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    let raf = 0;
    const handler = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        setScrolled(window.scrollY > SCROLL_REVEAL_PX);
        raf = 0;
      });
    };
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => { window.removeEventListener("scroll", handler); if (raf) cancelAnimationFrame(raf); };
  }, []);

  const visible = actions.filter((a) => a && (a.onClick || a.href) && !a.hidden);
  if (visible.length === 0 && !scrolled) return null;

  return (
    <div
      data-testid={testId}
      className="fixed pointer-events-none flex flex-col items-end gap-2"
      style={{
        // Side position (Defect 3) — clear of the bottom-nav (zIndex 10000,
        // ~68px tall) and clear of the content column, which is capped at
        // max-w-3xl and centered, so the right edge always has margin.
        right: "max(14px, env(safe-area-inset-right, 0px))",
        bottom: "calc(68px + env(safe-area-inset-bottom, 0px) + 14px)",
        zIndex: 10001,
      }}
    >
      {/* Back-to-Top — auto-injected, fades in after scroll */}
      <button
        type="button"
        data-testid="fab-top"
        aria-label="Back to top"
        title="Back to top"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className={`pointer-events-auto inline-flex items-center justify-center w-10 h-10 rounded-full text-[11px] font-medium shadow-2xl transition-all duration-300 ${scrolled ? "opacity-100 scale-100" : "opacity-0 scale-90 pointer-events-none"}`}
        style={{
          background: "rgba(14,16,20,0.92)",
          color: "var(--rs-text-2, #c2cad4)",
          border: "1px solid var(--rs-border, #1f2630)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
        }}
      >
        <ArrowUp className="w-4 h-4" />
      </button>

      {/* Context actions */}
      {visible.map((a) => <ToolbarButton key={a.id} action={a} />)}
    </div>
  );
}

function ToolbarButton({ action }) {
  const Icon = action.icon;
  const body = (
    <>
      {Icon && <Icon className="w-4 h-4" />}
      <span className="hidden sm:inline text-[11.5px] font-medium tracking-[0.02em]">
        {action.label}
      </span>
    </>
  );

  const base =
    "pointer-events-auto inline-flex items-center gap-1.5 h-10 px-3 sm:px-3.5 rounded-full shadow-2xl transition-colors";

  const primary = {
    background: "var(--rs-amber, #f5b400)",
    color: "#0A0B0E",
    border: "1px solid var(--rs-amber, #f5b400)",
  };
  const ghost = {
    background: "rgba(14,16,20,0.92)",
    color: "var(--rs-text-1, #e8edf2)",
    border: "1px solid var(--rs-border, #1f2630)",
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
  };

  if (action.href) {
    return (
      <Link
        data-testid={action.testId}
        to={action.href}
        aria-label={action.label}
        title={action.label}
        className={base + " hover:border-amber-400/60"}
        style={action.primary ? primary : ghost}
      >
        {body}
      </Link>
    );
  }

  return (
    <button
      type="button"
      data-testid={action.testId}
      aria-label={action.label}
      title={action.label}
      onClick={action.onClick}
      disabled={action.disabled}
      className={base + (action.primary ? " hover:brightness-110" : " hover:border-amber-400/60")}
      style={{
        ...(action.primary ? primary : ghost),
        opacity: action.disabled ? 0.45 : 1,
        cursor: action.disabled ? "not-allowed" : "pointer",
      }}
    >
      {body}
    </button>
  );
}
